import { Grid, makeStyles } from "@material-ui/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { loadingItem } from "../../redux/actions";
import { authSelector, itemSelector } from "../../redux/selectors";
import { Skeleton } from "@material-ui/lab";
import { Tabs } from "@ofqual/portal-components/components/Tabs";
import { ModuleAppBar } from "../ModuleAppBar";
import { ItemHeader } from "./ItemHeader";
import { ItemDetails } from "./ItemDetails";
import { ScrollableAreaTabs } from "@ofqual/portal-components/components/ScrollableAreaTabs";
import { ScopeApplicationState } from "../../domain/ScopeApplication";
import { NewApplicationContent } from "../create/NewApplicationContent";
import { ExpansionActivityFeed } from "./activityFeed/ExpansionActivityFeed";
import { UserRole } from "../../domain/userRole";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { useOrganisationState } from "../../hooks/useOrganisationState";
import { ExistingScopes } from "./ExistingScopes";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("lg")]: {
      marginTop: theme.contentOffsets.AFTER_RECORD_HEADER,
    },
    [theme.breakpoints.down("md")]: {
      marginTop: "25px", // theme.contentOffsets.AFTER_MAIN_APPBAR
    },
  },
}));

export const Item: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isInRole } = useAuth(authSelector);

  const { path, url } = useRouteMatch();
  const { push } = useHistory();

  const { itemId, id, tabId } = useParams<{
    itemId: string;
    id: string;
    tabId: string;
  }>();
  useEffect(() => {
    dispatch(loadingItem(itemId, id));
  }, [dispatch, id, itemId]);

  const state = useSelector(itemSelector);
  const orgState = useOrganisationState(itemId);

  const isRecognitionScopeAdmin = isInRole(
    UserRole.ITAdmin,
    UserRole.OfqualRecognitionExpansionAdmin
  );

  const isDisabled = state.item
    ? state.item.state === ScopeApplicationState.Complete ||
      state.item.state === ScopeApplicationState.Withdrawn
    : false;

  return (
    <div className={classes.root}>
      <ModuleAppBar breadcrumbs={[{ label: "" }]} />
      <ItemHeader state={state} isAdmin={isRecognitionScopeAdmin} />

      <Grid container spacing={3}>
        <Grid item md={8}>
          {state.item && orgState && orgState.org ? (
            <>
              <Tabs
                dataTest="scope-details-tabs"
                currentTabId={tabId || ""}
                tabs={
                  state.item.state === ScopeApplicationState.Draft
                    ? [{ id: "", label: "Application Details" }]
                    : [
                        { id: "", label: "Application Details" },
                        { id: "existing-scope", label: "Existing Scope" },
                      ]
                }
                onTabChange={(destinationTabId) => {
                  const rootUrl =
                    tabId === undefined
                      ? url // if we are on the root tab then the url is the root
                      : url.substring(0, url.length - tabId.length - 1); // otherwise get the root from taking the tabId away from the url

                  // if we are navigating to a tab (destinationTabId is something) then add it to the root
                  //  otherwise just go to the root (destinationTabId is empty)
                  const destination = destinationTabId
                    ? `${rootUrl}/${destinationTabId}`
                    : rootUrl;
                  push(destination);
                }}
              ></Tabs>
              <Switch>
                {state.item.state !== ScopeApplicationState.Draft && (
                  <Route path={`${path}/existing-scope`}>
                    <ExistingScopes
                      application={state.item}
                      orgName={orgState.org.name}
                    />
                  </Route>
                )}
                <Route exact path={`${path}/`}>
                  <ItemDetails
                    state={state}
                    orgName={orgState.org.name}
                    isAdmin={isRecognitionScopeAdmin}
                  />
                </Route>
              </Switch>
            </>
          ) : (
            <>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={60} />
              <br />
              <Skeleton variant="rect" height={118} />
            </>
          )}
        </Grid>

        <Grid item md={4} xs={12}>
          {state.item ? (
            state.item.state === ScopeApplicationState.Draft ? (
              <NewApplicationContent />
            ) : (
              <ScrollableAreaTabs
                tabs={[
                  {
                    label: "Activity",
                    content: (
                      <ExpansionActivityFeed
                        application={state.item}
                        evidenceRequests={state.evidenceRequests}
                        isAdmin={isRecognitionScopeAdmin}
                        disabled={isDisabled}
                      />
                    ),
                  },
                ]}
              ></ScrollableAreaTabs>
            )
          ) : (
            <>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={60} />
              <br />
              <Skeleton variant="rect" height={118} />
            </>
          )}
        </Grid>
      </Grid>
    </div>
  );
};
